import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Store, Save, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/contexts/TranslationContext';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface ShopFormData {
  shop_name: string;
  shop_url: string;
  shop_description: string;
  shop_logo_url: string;
  shopify_domain: string;
  is_public: boolean;
  is_active: boolean;
}

export default function ConnectShop() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [existingShop, setExistingShop] = useState<any>(null);
  const [formData, setFormData] = useState<ShopFormData>({
    shop_name: '',
    shop_url: '',
    shop_description: '',
    shop_logo_url: '',
    shopify_domain: '',
    is_public: true,
    is_active: true,
  });

  useEffect(() => {
    if (user) {
      fetchExistingShop();
    }
  }, [user]);

  const fetchExistingShop = async () => {
    try {
      const { data, error } = await supabase
        .from('user_shops')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (data) {
        setExistingShop(data);
        setFormData({
          shop_name: data.shop_name || '',
          shop_url: data.shop_url || '',
          shop_description: data.shop_description || '',
          shop_logo_url: data.shop_logo_url || '',
          shopify_domain: data.shopify_domain || '',
          is_public: data.is_public,
          is_active: data.is_active,
        });
      }
    } catch (error) {
      // No shop exists yet, which is fine
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const shopData = {
        user_id: user.id,
        ...formData,
      };

      if (existingShop) {
        // Update existing shop
        const { error } = await supabase
          .from('user_shops')
          .update(shopData)
          .eq('id', existingShop.id);

        if (error) throw error;
        toast.success(t('shop.updated'));
      } else {
        // Create new shop
        const { error } = await supabase
          .from('user_shops')
          .insert([shopData]);

        if (error) throw error;
        toast.success(t('shop.connected'));
      }

      navigate('/app/shop');
    } catch (error) {
      console.error('Error saving shop:', error);
      toast.error(existingShop ? t('shop.errorUpdating') : t('shop.errorConnecting'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!existingShop || !user) return;

    if (!confirm('Sei sicuro di voler eliminare il tuo negozio?')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('user_shops')
        .delete()
        .eq('id', existingShop.id);

      if (error) throw error;
      toast.success('Negozio eliminato con successo');
      navigate('/app/shop');
    } catch (error) {
      console.error('Error deleting shop:', error);
      toast.error('Errore nell\'eliminare il negozio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen gradient-secondary">
      {/* Header */}
      <div className="glass border-b border-primary/20 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link to="/app/shop">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('action.back')}
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                <Store className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {existingShop ? 'Modifica Negozio' : t('shop.connectYourStore')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {existingShop ? 'Aggiorna le informazioni del tuo negozio' : 'Collega il tuo negozio Shopify o altro e-commerce'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto p-4">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Informazioni Negozio</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Shop Name */}
              <div className="space-y-2">
                <Label htmlFor="shop_name" className="text-white">
                  {t('shop.shopName')} *
                </Label>
                <Input
                  id="shop_name"
                  value={formData.shop_name}
                  onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                  placeholder="es. Pet Store di Mario"
                  className="glass border-white/20 text-white"
                  required
                />
              </div>

              {/* Shop URL */}
              <div className="space-y-2">
                <Label htmlFor="shop_url" className="text-white">
                  {t('shop.shopUrl')} *
                </Label>
                <Input
                  id="shop_url"
                  type="url"
                  value={formData.shop_url}
                  onChange={(e) => setFormData({ ...formData, shop_url: e.target.value })}
                  placeholder="https://mio-negozio.myshopify.com"
                  className="glass border-white/20 text-white"
                  required
                />
              </div>

              {/* Shopify Domain */}
              <div className="space-y-2">
                <Label htmlFor="shopify_domain" className="text-white">
                  {t('shop.shopifyDomain')}
                </Label>
                <Input
                  id="shopify_domain"
                  value={formData.shopify_domain}
                  onChange={(e) => setFormData({ ...formData, shopify_domain: e.target.value })}
                  placeholder="mio-negozio.myshopify.com"
                  className="glass border-white/20 text-white"
                />
                <p className="text-xs text-muted-foreground">
                  Se usi Shopify, inserisci il dominio per una migliore integrazione
                </p>
              </div>

              {/* Shop Description */}
              <div className="space-y-2">
                <Label htmlFor="shop_description" className="text-white">
                  {t('shop.shopDescription')}
                </Label>
                <Textarea
                  id="shop_description"
                  value={formData.shop_description}
                  onChange={(e) => setFormData({ ...formData, shop_description: e.target.value })}
                  placeholder="Descrivi il tuo negozio e i prodotti che vendi..."
                  className="glass border-white/20 text-white resize-none"
                  rows={4}
                />
              </div>

              {/* Logo URL */}
              <div className="space-y-2">
                <Label htmlFor="shop_logo_url" className="text-white">
                  {t('shop.logoUrl')}
                </Label>
                <Input
                  id="shop_logo_url"
                  type="url"
                  value={formData.shop_logo_url}
                  onChange={(e) => setFormData({ ...formData, shop_logo_url: e.target.value })}
                  placeholder="https://esempio.com/logo.png"
                  className="glass border-white/20 text-white"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Impostazioni Privacy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Public Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-white">{t('shop.makePublic')}</Label>
                  <p className="text-sm text-muted-foreground">
                    Permetti ad altri utenti di vedere il tuo negozio
                  </p>
                </div>
                <Switch
                  checked={formData.is_public}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                />
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-white">{t('shop.isActive')}</Label>
                  <p className="text-sm text-muted-foreground">
                    Il negozio è attualmente operativo
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? t('action.saving') : (existingShop ? t('action.save') : t('shop.connectYourStore'))}
            </Button>

            {existingShop && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={loading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('button.delete')}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}